import type { Record } from '@servicenow/sdk-build-core'
import { NowIdShape } from '@servicenow/sdk-build-plugins'
import { OOB_CHANNEL_MAP, PUBLIC_ACCESS_ROLE_SYS_ID, USER_IS_AUTHENTICATED } from './constants'

/**
 * Reverse mapping: sys_id to OOB name for channels
 */
const CHANNEL_SYS_ID_TO_NAME_MAP: { [key: string]: string } = Object.fromEntries(
    Object.entries(OOB_CHANNEL_MAP).map(([name, sysId]) => [sysId, name])
)

/**
 * Reverse maps a channel sys_id to its OOB name
 * @param sysId - The channel sys_id from ServiceNow
 * @returns The OOB name if found, otherwise the original sys_id
 */
export const reverseMapChannel = (sysId: string | undefined): string | undefined => {
    if (!sysId) {
        return undefined
    }
    return CHANNEL_SYS_ID_TO_NAME_MAP[sysId] || sysId
}

/**
 * Helper function to compute team value with nested members from descendant records
 * @param teamRecord - The team record from descendants
 * @param teamMembers - The team member records from descendants
 */
export const computeTeamValue = (teamRecord: Record | undefined, teamMembers: Record[]) => {
    if (!teamRecord) {
        return undefined
    }

    const sysDomainVal = teamRecord.get('sys_domain')?.ifString()?.getValue()

    return {
        $id: NowIdShape.from(teamRecord) ?? teamRecord.getId().getValue(),
        name: teamRecord.get('name')?.ifString()?.getValue(),
        sysDomain: sysDomainVal !== 'global' ? sysDomainVal : undefined,
        members: teamMembers.length > 0 ? teamMembers.map((tm) => tm.get('agent')?.ifString()?.getValue()) : undefined,
    }
}

/**
 * Helper function to compute version details value from version records
 * @param versions - Array of version records from descendants
 */
export const computeVersionDetailsValue = (versions: Record[]) => {
    if (!versions || versions.length === 0) {
        return undefined
    }
    return versions.map((v) => {
        const versionNumberStr = v.get('version_number')?.ifString()?.getValue()
        const versionNumber =
            versionNumberStr && versionNumberStr !== '' && !Number.isNaN(Number(versionNumberStr))
                ? Number(versionNumberStr)
                : 1

        return v.transform(({ $ }) => ({
            name: $.from('version_name'),
            number: $.val(versionNumber),
            state: $.from('state').def('draft'),
            instructions: $.from('instructions'),
            condition: $.from('condition').def(''),
        }))
    })
}

export { computeDataAccessValue } from '../shared/data-access-transform-helper'

/**
 * Helper function to map trigger config records to transformed shapes using record.transform()
 * Uses .def() to suppress default values, following the RestApi plugin pattern.
 * @param triggerConfigs - Array of trigger configuration records from descendants
 * @param triggerMappings - Array of trigger mapping records (M2M) from descendants
 */
export const mapTriggerConfigs = (triggerConfigs: Record[], triggerMappings: Record[]) => {
    if (triggerConfigs.length === 0) {
        return []
    }

    return triggerConfigs.map((tc) => {
        // Find corresponding trigger mapping for sys_overrides
        const triggerConfigId = tc.getId().getValue()
        const triggerMapping = triggerMappings.find((tm) => {
            const tmTriggerConfig = tm.get('trigger_configuration')
            if (tmTriggerConfig?.isRecord()) {
                return tmTriggerConfig.getId().getValue() === triggerConfigId
            }
            return tmTriggerConfig?.ifString()?.getValue() === triggerConfigId
        })

        // Compute values that need special handling before transform
        const channelValue = reverseMapChannel(tc.get('channel')?.ifString()?.getValue())
        const sysOverridesValue = triggerMapping?.get('sys_overrides')?.ifString()?.ifNotEmpty()?.getValue()

        // Build schedule object only if any schedule field has a value
        const runDayOfMonthValue = tc.get('run_dayofmonth')?.ifString()?.ifNotEmpty()?.toNumber()?.getValue()
        const runDayOfWeekValue = tc.get('run_dayofweek')?.ifString()?.ifNotEmpty()?.toNumber()?.getValue()
        const repeatIntervalValue = tc.get('run_period')?.ifString()?.ifNotEmpty()?.getValue()
        const startingValue = tc.get('run_start')?.ifString()?.ifNotEmpty()?.getValue()
        const timeValue = tc.get('run_time')?.ifString()?.ifNotEmpty()?.getValue()
        const rawTriggerStrategy = tc.get('trigger_strategy')?.ifString()?.ifNotEmpty()?.getValue()
        const triggerStrategyValue = rawTriggerStrategy === 'undefined' ? undefined : rawTriggerStrategy

        const schedule =
            runDayOfMonthValue ||
            runDayOfWeekValue ||
            repeatIntervalValue ||
            startingValue ||
            timeValue ||
            triggerStrategyValue
                ? {
                      ...(runDayOfMonthValue && { runDayOfMonth: runDayOfMonthValue }),
                      ...(runDayOfWeekValue && { runDayOfWeek: runDayOfWeekValue }),
                      ...(repeatIntervalValue && { repeatInterval: repeatIntervalValue }),
                      ...(startingValue && { starting: startingValue }),
                      ...(timeValue && { time: timeValue }),
                      ...(triggerStrategyValue && { triggerStrategy: triggerStrategyValue }),
                  }
                : undefined

        const portalValue = tc.get('portal')?.ifString()?.getValue()
        const profileValue = tc.get('profile')?.ifString()?.getValue()

        return tc.transform(({ $ }) => ({
            name: $,
            description: $.def(''),
            active: $.toBoolean().def(false),
            channel: $.val(channelValue),
            triggerCondition: $.from('condition').def(''),
            enableDiscovery: $.from('enable_discovery').toBoolean().def(false),
            notificationScript: $.from('notification_script').def(''),
            objectiveTemplate: $.from('objective_template').def(''),
            ...(portalValue && portalValue !== 'undefined' ? { portal: $.from('portal') } : {}),
            ...(profileValue && profileValue !== 'undefined' ? { profile: $.from('profile') } : {}),
            runAs: $.from('run_as').def(''),
            runAsScript: $.from('run_as_script').def(''),
            runAsUser: $.from('run_as_user').def(''),
            businessRule: $.from('business_rule').def(''),
            showNotifications: $.from('show_notifications').toBoolean().def(false),
            domain: $.from('sys_domain').def('global'),
            targetTable: $.from('target_table'),
            triggerFlowDefinitionType: $.from('trigger_flow_definition_type'),
            schedule: $.val(schedule),
            sysOverrides: $.val(sysOverridesValue),
        }))
    })
}

/**
 * Helper function to reconstruct a securityAcl value from a sys_security_acl record
 * and its associated sys_security_acl_role records.
 *
 * Used in toShape to reverse-map ACL records back to the SecurityAclUserAccessConfig API shape.
 * Returns undefined if no ACL record is provided.
 *
 * @param aclRecord      - The sys_security_acl record found for this workflow
 * @param aclRoleRecords - All sys_security_acl_role records linked to that ACL
 */
export const computeSecurityAclValue = (aclRecord: Record | undefined, aclRoleRecords: Record[]) => {
    if (!aclRecord) {
        return undefined
    }

    const securityAttribute = aclRecord.get('security_attribute')?.ifString()?.getValue()
    const isAuthenticated = securityAttribute === USER_IS_AUTHENTICATED

    // Collect role values from the ACL role records (prefer sys_id string),
    // excluding the sentinel public-access role which signals type='Public'
    const allRoles = aclRoleRecords
        .map((r) => {
            const roleRef = r.get('sys_user_role')
            if (roleRef?.isRecord()) {
                return roleRef.getId().getValue()
            }
            return roleRef?.ifString()?.getValue()
        })
        .filter((v): v is string => !!v)

    const isPublic = allRoles.length > 0 && allRoles.every((r) => r === PUBLIC_ACCESS_ROLE_SYS_ID)
    const roles = allRoles.filter((r) => r !== PUBLIC_ACCESS_ROLE_SYS_ID)

    const $id = NowIdShape.from(aclRecord)

    if (isAuthenticated) {
        return {
            $id,
            type: 'Any authenticated user' as const,
        }
    }

    if (isPublic || roles.length === 0) {
        return {
            $id,
            type: 'Public' as const,
        }
    }

    return {
        $id,
        type: 'Specific role' as const,
        roles,
    }
}
